/**
 * Terms and Conditions List Page
 */
import { useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, ConfirmDialog } from '../../components/common';
import {
  useGetTermsAdminQuery,
  useDeleteTermsMutation,
  TermsListItem,
} from '../../store/api/termsApi';
import { usePermissions } from '../../hooks/usePermissions';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const TermsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState<TermsListItem | null>(null);

  const canManage = hasPermission('manage_ads');
  const canDelete = hasPermission('delete_ads');

  const { data, isLoading, error } = useGetTermsAdminQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
  });

  const [deleteTerms, { isLoading: isDeleting }] = useDeleteTermsMutation();

  const handleDelete = async () => {
    if (!selectedTerms) return;
    try {
      await deleteTerms(selectedTerms.id).unwrap();
      toast.success(t('terms.toast.deleteSuccess'));
      setDeleteDialog(false);
      setSelectedTerms(null);
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      toast.error(e?.data?.message || t('terms.toast.deleteError'));
    }
  };

  const columns: Column<TermsListItem>[] = [
    {
      id: 'title',
      label: t('terms.cols.title'),
      minWidth: 250,
      render: (row) => (
        <Box sx={{ fontWeight: 600, color: 'primary.main' }}>{row.title}</Box>
      ),
    },
    {
      id: 'updated_at',
      label: t('terms.cols.updated'),
      minWidth: 150,
      render: (row) => moment(row.updated_at).format('MMM DD, YYYY'),
    },
    {
      id: 'actions',
      label: t('common.actions'),
      minWidth: 140,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/terms/${row.id}`)}
            color="primary"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          {canManage && (
            <IconButton
              size="small"
              onClick={() => navigate(`/terms/${row.id}/edit`)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => {
                setSelectedTerms(row);
                setDeleteDialog(true);
              }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('terms.title')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.terms') },
        ]}
        action={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={t('common.search')}
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
            {canManage && (
              <IconButton
                color="primary"
                onClick={() => navigate('/terms/create')}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        }
      />

      <Card sx={{ mt: 3 }}>
        <DataTable
          columns={columns}
          data={data?.results || []}
          loading={isLoading}
          error={error ? (typeof error === 'string' ? error : t('terms.toast.genericError')) : null}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          totalCount={data?.count || 0}
        />
      </Card>

      <ConfirmDialog
        open={deleteDialog}
        title={t('terms.dialog.deleteTitle')}
        message={t('terms.dialog.deleteMessage', { title: selectedTerms?.title })}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialog(false);
          setSelectedTerms(null);
        }}
        loading={isDeleting}
      />
    </Box>
  );
};

export default TermsListPage;
